import classNames from 'clsx';
import React, { type FC, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { enabledPublicDownloadLimits, tooltipTrackDownload } from 'reactApp/appHelpers/featuresHelpers/features/publicDownloadLimits';
import { storeHelper } from 'reactApp/modules/promo/promo.helpers';
import { getFirstPublicWithDownloadLimits, getPublicLimits } from 'reactApp/modules/public/public.selectors';
import { EViewMode } from 'reactApp/modules/settings/settings.types';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import type { RootState } from 'reactApp/store';
import { Checkbox } from 'reactApp/ui/DataListItemCommonComponents/Checkbox/Checkbox';
import { Download } from 'reactApp/ui/DataListItemCommonComponents/Download/Download';
import { Favorite } from 'reactApp/ui/DataListItemCommonComponents/Favorite/Favorite';
import { Progress } from 'reactApp/ui/DataListItemCommonComponents/Progress/Progress';
import { Publish } from 'reactApp/ui/DataListItemCommonComponents/Publish/Publish';
import { Date } from 'reactApp/ui/DataListItemRow/components/Date/Date';
import { Expires } from 'reactApp/ui/DataListItemRow/components/Expires/Expires';
import { ItemName } from 'reactApp/ui/DataListItemRow/components/ItemName/ItemName';
import { ItemTip } from 'reactApp/ui/DataListItemRow/components/ItemTip/ItemTip';
import { Size } from 'reactApp/ui/DataListItemRow/components/Size/Size';
import { TableColumn } from 'reactApp/ui/DataListItemRow/components/TableColumn/TableColumn';
import type { HomeItemProps } from 'reactApp/ui/DataListItemRow/DataListItemRow.types';
import { FilePic } from 'reactApp/ui/FilePic/FilePic';
import { TrackDownloadCountPromo } from 'reactApp/ui/SharingTooltips/TrackDownloadCountPromo';
import { useCompactView } from 'reactApp/ui/VirtualList/VirtualList.hooks';

import styles from '../../DataListItemRow.new.css';

// eslint-disable-next-line max-lines-per-function
export const HomeItem: FC<HomeItemProps> = (props) => {
    const {
        id,
        storage,
        denyDoAction: isProgress,
        tipData,
        isFolder,
        progress,
        renameAvailable,
        thumbUrl,
        isVirus,
        kind,
        isPublic,
        isSeoEnabled,
        showPublishButton,
        showDownloadButton,
        name,
        size,
        mtime,
        author,
        ext,
        favoritesAvailable,
        isAutoDelete,
        publicExpires,
        isSelected,
        isInFavorites,
        onCheckboxClick,
        onCancelUpload,
        onFavorites,
        onDownload,
        onPublish,
        weblinkUrl,
    } = props;
    const tooltipRef = useRef<HTMLDivElement>(null);

    const isCompact = useCompactView();

    const tipTargetRef = useRef(null);

    // tempexp_17847-start
    const TOOLTIP_ID = 'tooltipTrackDownload';
    const isTooltipShown = Boolean(storeHelper.getValue(TOOLTIP_ID));
    const [showTooltipCounter, setShowTooltipCounter] = useState(false);
    const isPaidUser = useSelector(UserSelectors.isPaidUser);
    const downloadLimits = useSelector((state: RootState) => getPublicLimits(state, weblinkUrl || ''));
    const downloadTotal = downloadLimits && downloadLimits.total;
    const downloadLeft = downloadLimits && downloadLimits.left;
    const downloadCount = downloadTotal - downloadLeft;
    const showDownloadCounter = enabledPublicDownloadLimits && storage === EStorageType.home && !isPaidUser && downloadCount > 0;

    const firstPublicWithDownloads = useSelector((state: RootState) => getFirstPublicWithDownloadLimits(state));
    const weblinkForPromo = firstPublicWithDownloads && Object.keys(firstPublicWithDownloads)[0];
    const showTrackDownloadCountPromo = tooltipTrackDownload && showDownloadCounter;
    const showFirstTrackDownloadCountPromo = showTrackDownloadCountPromo && weblinkUrl === weblinkForPromo;

    const handleCounterClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setShowTooltipCounter(true);
    };

    const handleCounterPromoHide = () => {
        setShowTooltipCounter(false);
    };
    // tempexp_17847-end

    return (
        <>
            <ItemTip target={tipTargetRef} tipData={tipData} isFolder={isFolder ?? false} />
            <TableColumn>
                <Checkbox
                    viewType={EViewMode.list}
                    className={styles.check}
                    onCheckboxClick={onCheckboxClick}
                    isSelected={isSelected}
                    isDisabled={isProgress}
                />
                <FilePic
                    ref={tipTargetRef}
                    isFolder={isFolder}
                    thumbUrl={thumbUrl}
                    kind={kind}
                    isVirus={isVirus}
                    ext={ext || ''}
                    author={author}
                    isAutoDelete={isAutoDelete}
                    publicExpires={publicExpires}
                    isPublic={isPublic}
                />
            </TableColumn>

            {isCompact ? (
                <TableColumn width="100%" overflow="hidden">
                    <div className={styles.nameWrapper}>
                        <ItemName name={name} ext={ext || ''} loading={isProgress} id={id} saveFormatting />
                        <span className={styles.responsiveDateWrapper}>
                            <Size size={size} isInline />
                            {!!mtime && <>&nbsp;•&nbsp;</>}
                            <Date date={mtime} className={styles.date} />
                        </span>
                        <Expires expires={publicExpires} isAutoDelete={isAutoDelete} isShared={!!publicExpires} isInline />
                    </div>
                </TableColumn>
            ) : (
                <>
                    <TableColumn width="100%" overflow="hidden">
                        <ItemName
                            name={name}
                            ext={ext || ''}
                            loading={isProgress}
                            id={id}
                            isRenameAvailable={renameAvailable}
                            saveFormatting
                        />
                    </TableColumn>
                    {isFolder && Boolean(publicExpires) && (
                        <TableColumn flexBasis={214} flexShrink={0} justifyContent="flex-start">
                            <Expires expires={publicExpires} isAutoDelete={isAutoDelete} />
                        </TableColumn>
                    )}
                    {!isFolder && (
                        <TableColumn flexBasis={140} flexShrink={0} justifyContent="flex-end">
                            <Date date={mtime} />
                        </TableColumn>
                    )}
                    <TableColumn flexBasis={132} flexShrink={0} justifyContent="center">
                        <Size size={size} />
                    </TableColumn>
                </>
            )}

            {!isProgress && (
                <>
                    <TableColumn flexBasis={112} flexShrink={0} justifyContent="flex-end" gap={20}>
                        <Favorite
                            className={styles.favoriteAction}
                            isAvailable={favoritesAvailable}
                            isFavorite={isInFavorites}
                            onClick={onFavorites}
                            viewType={EViewMode.list}
                        />
                        {showPublishButton && (
                            <Publish
                                showPublicButton={props.showPublicButton}
                                viewType={EViewMode.list}
                                onClick={onPublish}
                                isShared={isPublic}
                                isSeoEnabled={isSeoEnabled}
                                itemId={id}
                            />
                        )}
                        {showDownloadButton && (
                            <Download
                                isAchivedLimit={downloadLeft === 0}
                                onClick={onDownload}
                                ext={isFolder ? 'zip' : ext}
                                size={size}
                                id={id}
                                storage={storage}
                                viewType={EViewMode.list}
                            />
                        )}
                    </TableColumn>
                    <div
                        ref={tooltipRef}
                        className={classNames(styles.counter, {
                            [styles.counterAchived]: enabledPublicDownloadLimits && downloadLeft === 0,
                            [styles.counterZero]: enabledPublicDownloadLimits && downloadLeft === downloadTotal,
                        })}
                        onClick={handleCounterClick}
                    >
                        {<div className={classNames(styles.counterText)}>{showDownloadCounter ? downloadCount : null}</div>}
                    </div>
                    {showFirstTrackDownloadCountPromo && !isTooltipShown && (
                        <TrackDownloadCountPromo targetRef={tooltipRef} downloads={downloadCount} downloadLimit={downloadTotal} />
                    )}
                    {showTrackDownloadCountPromo && showTooltipCounter && (
                        <TrackDownloadCountPromo
                            targetRef={tooltipRef}
                            downloads={downloadCount}
                            downloadLimit={downloadTotal}
                            onHide={handleCounterPromoHide}
                        />
                    )}
                </>
            )}
            {isProgress && typeof progress === 'number' && (
                <TableColumn flexBasis={100} flexShrink={0} justifyContent="flex-end" gap={20}>
                    <Progress onClick={onCancelUpload} progress={progress} />
                </TableColumn>
            )}
        </>
    );
};
